import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import {
  ANONYMOUS_COLLABORATION_TOKEN,
  type CloseEvent,
  COLLABORATION_CLOSE_REASONS,
  COLLABORATION_POLICY_CLOSE_CODE,
} from "@workith/shared";
import type * as Y from "yjs";
import {
  type CollaborationCredential,
  type CollaborationCredentialBroker,
  createCollaborationCredentialBroker,
} from "./collaborationCredentialBroker";

const OPEN_HANDSHAKE_DELAY_MS = 250;
const INITIAL_CONNECTION_TIMEOUT_MS = 10_000;
const WEBSOCKET_OPEN_STATE = 1;

export type CollaborationProvider = HocuspocusProvider;
export type CollaborationWebsocketProvider = HocuspocusProviderWebsocket;
export type { CollaborationCredential };

/**
 * How a logical room ended.
 *
 * Hocuspocus reports an in-band room close and a physical socket close through
 * the same `onClose` callback, so the reason string is what separates them: the
 * server closes a room with one of the stable `COLLABORATION_CLOSE_REASONS`
 * without touching the shared socket, while a transport failure carries
 * whatever the browser reports. Classifying them apart is what keeps a
 * revocation from being retried like a network blip - and a network blip from
 * being shown as "access denied".
 */
export type CollaborationCloseClassification =
  | "authorization-denied"
  | "authorization-downgraded"
  | "authorization-revoked"
  | "lease-expired"
  | "section-unavailable"
  | "room-limit-exceeded"
  | "detached"
  | "transport";

export interface CollaborationCloseInfo {
  readonly classification: CollaborationCloseClassification;
  /**
   * `document`: the parent document ACL or the principal itself, so every room
   * of this document is affected. `room`: this room's own topology or lease.
   * `transport`: the socket, no authorization meaning. `local`: this client
   * detached on purpose.
   */
  readonly scope: "document" | "room" | "transport" | "local";
  /** Whether this room is over: nothing this client sends will be admitted. */
  readonly terminal: boolean;
  /** Whether reconnecting can resolve it. Only transport loss is retryable. */
  readonly retryable: boolean;
  /** Whether it is an authorization answer rather than a transport symptom. */
  readonly authorization: boolean;
  readonly event: CloseEvent;
}

const CLOSE_CLASSIFICATIONS: Readonly<Record<string, CollaborationCloseClassification>> = {
  [COLLABORATION_CLOSE_REASONS.authorizationDenied]: "authorization-denied",
  [COLLABORATION_CLOSE_REASONS.authorizationDowngraded]: "authorization-downgraded",
  [COLLABORATION_CLOSE_REASONS.authorizationRevoked]: "authorization-revoked",
  [COLLABORATION_CLOSE_REASONS.leaseExpired]: "lease-expired",
  [COLLABORATION_CLOSE_REASONS.sectionUnavailable]: "section-unavailable",
  [COLLABORATION_CLOSE_REASONS.roomLimitExceeded]: "room-limit-exceeded",
};

const DOCUMENT_SCOPED: ReadonlySet<CollaborationCloseClassification> = new Set([
  "authorization-denied",
  "authorization-downgraded",
  "authorization-revoked",
]);

/**
 * Maps a close frame onto the client's authorization/transport axes.
 *
 * `detached` wins over everything: a room this client tore down on purpose - a
 * removed section, or an old provider released before the same room name is
 * attached again - must never be reported as an authorization failure.
 */
export function classifyCollaborationClose(
  event: CloseEvent,
  options: { detached?: boolean } = {}
): CollaborationCloseInfo {
  if (options.detached) {
    return {
      classification: "detached",
      scope: "local",
      terminal: true,
      retryable: false,
      authorization: false,
      event,
    };
  }

  const classification = CLOSE_CLASSIFICATIONS[event.reason];
  if (!classification) {
    return {
      classification: "transport",
      scope: "transport",
      terminal: false,
      retryable: true,
      authorization: false,
      event,
    };
  }

  return {
    classification,
    scope: DOCUMENT_SCOPED.has(classification) ? "document" : "room",
    terminal: true,
    // A logical close leaves the socket up, so there is nothing to retry: the
    // server has already made this room terminal and will ignore a re-sync.
    retryable: false,
    authorization: true,
    event,
  };
}

interface CollaborationProviderBaseOptions {
  documentId: string;
  /**
   * Hocuspocus room to join. Defaults to `documentId`; a `sectioned-v1` section
   * passes `sectionRoomName(documentId, sectionId)` while keeping the parent
   * document in the connection URL so the server route context stays the parent.
   */
  roomName?: string;
  /**
   * Y.Doc to bind. Section providers must pass the very subdocument object held
   * by the root manifest Y.Array, never a fresh Y.Doc.
   */
  document?: Y.Doc;
  /**
   * Whether an internally created broker drives the renewal clock. Ignored when
   * `credentialBroker` is supplied: a shared broker schedules once for the whole
   * document, so no room may run a second clock.
   */
  manageLeaseRenewal?: boolean;
  /** The parent-document broker every room of this document shares. */
  credentialBroker?: CollaborationCredentialBroker;
  /** Token callback boundary; only used when no broker is supplied. */
  tokenProvider?: () => Promise<CollaborationCredential>;
  onConnect?: () => void;
  /** Physical transport loss. Never fires for an in-band room close. */
  onDisconnect: (data: { event: CloseEvent }) => void;
  /** Logical room close, classified onto the authorization/transport axes. */
  onClose?: (info: CollaborationCloseInfo) => void;
  onAuthenticated: (provider: CollaborationProvider) => void;
  onSynced: () => void;
  onAwarenessChange: () => void;
  onConnectionTimeout?: () => void;
  connectionTimeoutMs?: number;
}

/**
 * A provider either opens its own socket from `url` or joins a socket the caller
 * already owns - never both. Expressing that as a union means an invalid call
 * fails to compile instead of throwing at connect time, and it is what makes the
 * ownership rule in {@link CollaborationProviderBundle.destroy} checkable: a
 * shared socket is closed by whoever created it, exactly once.
 */
type CreateCollaborationProviderOptions = CollaborationProviderBaseOptions &
  (
    | { url: string; websocketProvider?: undefined }
    | { url?: undefined; websocketProvider: CollaborationWebsocketProvider }
  );

/** Creates the socket that every room of one document shares. */
export function createCollaborationWebsocket(url: string): CollaborationWebsocketProvider {
  return new HocuspocusProviderWebsocket({ url, autoConnect: false });
}

export interface CollaborationProviderBundle {
  provider: CollaborationProvider;
  websocketProvider: CollaborationWebsocketProvider;
  /**
   * The name this room occupies on the shared socket.
   *
   * `sessionAwareness` is off, so Hocuspocus puts the room name on the wire
   * verbatim instead of a `name\0sessionId` composite. That is deliberate: room
   * names are already unique per document (`documentId` for the root,
   * `sectionRoomName()` for a section), so multiplexing needs no session key -
   * and it means two providers carrying this name on one socket would be an
   * actual collision rather than two harmless sessions.
   */
  effectiveRoomName: string;
  /** The broker this room renews through, shared or internally created. */
  credentialBroker: CollaborationCredentialBroker;
  connect: () => Promise<unknown>;
  /** Leaves the room on purpose; later close frames classify as `detached`. */
  detach: () => void;
  destroy: () => void;
}

/**
 * Adds two things the stock provider has no seam for: a delayed open handshake,
 * and an outbound admission barrier used while the credential is being renewed
 * after a page resume.
 */
class ManagedHocuspocusProvider extends HocuspocusProvider {
  private openDelays = new Set<{
    timer: ReturnType<typeof setTimeout>;
    resolve: () => void;
  }>();
  private destroyed = false;
  private outboundPaused = false;
  private outboundBlocked = false;
  private heldUpdates: Array<{ update: Uint8Array; origin: unknown }> = [];

  override async onOpen(event: Parameters<HocuspocusProvider["onOpen"]>[0]): Promise<void> {
    await new Promise<void>((resolve) => {
      const delay = {
        timer: setTimeout(() => {
          this.openDelays.delete(delay);
          resolve();
        }, OPEN_HANDSHAKE_DELAY_MS),
        resolve,
      };
      this.openDelays.add(delay);
    });

    if (
      this.destroyed ||
      !this.isAttached ||
      !isWebSocketOpen(this.configuration.websocketProvider)
    ) {
      return;
    }

    await super.onOpen(event);
  }

  /**
   * Holds local document updates. The authentication message is deliberately
   * *not* held: reauthorization has to reach the server first, and because the
   * socket preserves order, the held updates that replay afterwards are
   * evaluated under the renewed lease.
   */
  pauseOutbound(): void {
    this.outboundPaused = true;
  }

  /**
   * Permanent counterpart of {@link pauseOutbound}, for a room the server has
   * already refused.
   *
   * Nothing is held, because nothing will ever replay: the room is terminal, so
   * a held queue could only grow - the IME-finish window keeps producing local
   * updates until read-only lands - and then be discarded. Dropping loses
   * nothing, since every one of those updates is already in the local Y.Doc,
   * which is exactly where U7's recovery export reads from.
   */
  blockOutbound(): void {
    this.outboundBlocked = true;
    this.heldUpdates = [];
  }

  resumeOutbound(): void {
    // A blocked room is never resumed, whoever asks: the server refused it, and
    // replaying onto it would lift an admission barrier that must stay closed.
    if (this.outboundBlocked || !this.outboundPaused) return;
    this.outboundPaused = false;
    const held = this.heldUpdates;
    this.heldUpdates = [];
    for (const { update, origin } of held) {
      super.documentUpdateHandler(update, origin);
    }
  }

  override documentUpdateHandler(update: Uint8Array, origin: unknown): void {
    if (origin !== this) {
      if (this.outboundBlocked) return;
      if (this.outboundPaused) {
        this.heldUpdates.push({ update, origin });
        return;
      }
    }

    super.documentUpdateHandler(update, origin);
  }

  override destroy(): void {
    this.destroyed = true;
    this.heldUpdates = [];
    for (const delay of this.openDelays) {
      clearTimeout(delay.timer);
      delay.resolve();
    }
    this.openDelays.clear();

    super.destroy();
  }
}

function isWebSocketOpen(websocketProvider: HocuspocusProviderWebsocket): boolean {
  return websocketProvider.webSocket?.readyState === WEBSOCKET_OPEN_STATE;
}

export function createCollaborationProvider({
  url,
  documentId,
  roomName = documentId,
  document,
  websocketProvider: sharedWebsocketProvider,
  manageLeaseRenewal = true,
  credentialBroker: sharedCredentialBroker,
  tokenProvider,
  onConnect,
  onDisconnect,
  onClose,
  onAuthenticated,
  onSynced,
  onAwarenessChange,
  onConnectionTimeout,
  connectionTimeoutMs = INITIAL_CONNECTION_TIMEOUT_MS,
}: CreateCollaborationProviderOptions): CollaborationProviderBundle {
  const ownsWebsocketProvider = !sharedWebsocketProvider;
  const websocketProvider = sharedWebsocketProvider ?? createCollaborationWebsocket(url);
  const ownsCredentialBroker = !sharedCredentialBroker;
  const credentialBroker =
    sharedCredentialBroker ??
    createCollaborationCredentialBroker({
      documentId,
      tokenProvider: tokenProvider ?? (async () => ANONYMOUS_COLLABORATION_TOKEN),
      schedule: manageLeaseRenewal,
    });

  let connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  let detached = false;
  let unregisterFromBroker: (() => void) | null = null;

  const clearConnectionTimeout = () => {
    if (!connectionTimeout) {
      return;
    }

    clearTimeout(connectionTimeout);
    connectionTimeout = null;
  };

  const startConnectionTimeout = () => {
    clearConnectionTimeout();

    if (!onConnectionTimeout) {
      return;
    }

    connectionTimeout = setTimeout(() => {
      connectionTimeout = null;

      if (!provider.synced) {
        onConnectionTimeout();
      }
    }, connectionTimeoutMs);
  };

  /**
   * Renewal is in band: the credential is presented on the live socket and the
   * server answers by keeping the room or closing it. There is no ACK to wait
   * for, and nothing here disconnects - provider, socket and Y.Doc identity all
   * survive a lease boundary (R4).
   */
  const handleClose = (event: CloseEvent) => {
    const info = classifyCollaborationClose(event, { detached });
    if (info.terminal && info.authorization) {
      // Admission stops here, before any UI reacts: whatever the user is still
      // typing - an IME composition mid-syllable included - stays in the local
      // Y.Doc for recovery and is never offered to a server that has already
      // refused this room. Nothing resumes it; the room is over.
      provider.blockOutbound();
    }
    if (info.terminal) {
      // A terminal room stops renewing: retrying a revoked credential would be
      // a request storm that can never succeed (AE5).
      unregisterFromBroker?.();
      unregisterFromBroker = null;
      if (info.scope === "document" && ownsCredentialBroker) credentialBroker.suspend();
    }
    // Before the room leaves the socket: U7 reads the refused content out of the
    // still-live Y.Doc from here, and the plan forbids tearing anything down
    // before that snapshot exists. Detaching does not touch the Y.Doc anyway,
    // but the ordering is what makes that independent of a future detach.
    onClose?.(info);
    if (info.terminal && !detached) {
      // Leaving the room is what actually ends the reauthorization storm. The
      // provider stays subscribed to the shared socket's `open` event until it
      // detaches, and Hocuspocus calls `sendToken()` + `startSync()` on every
      // attached provider each time that socket opens - and the socket reopens
      // on its own after any blip, a server restart, or the 30초 no-message
      // force-close. A refused room would re-present a credential the server has
      // already rejected, forever (R7/R17/AE5).
      //
      // Scope is exactly this room: `detach()` unsubscribes this provider and
      // sends its own close frame. Sibling rooms and the shared socket are
      // untouched, so a room-local `section-unavailable` cannot take the
      // document down, and the socket is still destroyed exactly once by the
      // caller that created it.
      detached = true;
      provider.detach();
    }
  };

  let provider: ManagedHocuspocusProvider;
  provider = new ManagedHocuspocusProvider({
    websocketProvider,
    name: roomName,
    // See CollaborationProviderBundle.effectiveRoomName: unique room names make
    // a session key unnecessary, and keeping it off is what lets a duplicate
    // attachment be detected by name.
    sessionAwareness: false,
    ...(document ? { document } : {}),
    token: () => credentialBroker.getToken(),
    // An undefined callback in this config overwrites the Hocuspocus default and
    // is registered as a websocket listener, crashing on the connect event.
    ...(onConnect ? { onConnect } : {}),
    onDisconnect,
    onClose: ({ event }) => handleClose(event),
    // Hocuspocus raises this both for a server permission denial and for a token
    // callback that refused to produce a credential - a broker suspended after
    // revocation does exactly that. Either way it is an authorization answer, so
    // a reason string that is not one of the stable server reasons still falls
    // back to `authorizationDenied` rather than to retryable transport loss.
    onAuthenticationFailed: ({ reason }) =>
      handleClose({
        code: COLLABORATION_POLICY_CLOSE_CODE,
        reason: CLOSE_CLASSIFICATIONS[reason]
          ? reason
          : COLLABORATION_CLOSE_REASONS.authorizationDenied,
      }),
    onAuthenticated: () => onAuthenticated(provider),
    onSynced: ({ state }) => {
      if (state) {
        clearConnectionTimeout();
        onSynced();
      }
    },
  });

  unregisterFromBroker = credentialBroker.register({
    roomName,
    sendToken: () => provider.sendToken(),
    pauseOutbound: () => provider.pauseOutbound(),
    resumeOutbound: () => provider.resumeOutbound(),
    resync: () => provider.forceSync(),
  });

  provider.awareness?.on("change", onAwarenessChange);
  provider.attach();

  const releaseBroker = () => {
    unregisterFromBroker?.();
    unregisterFromBroker = null;
    if (ownsCredentialBroker) credentialBroker.destroy();
  };

  return {
    provider,
    websocketProvider,
    effectiveRoomName: roomName,
    credentialBroker,
    connect: () => {
      startConnectionTimeout();
      // A shared socket is opened once for the whole document: a room joining
      // later rides the existing connection instead of reopening it.
      if (!ownsWebsocketProvider && websocketProvider.webSocket) {
        return Promise.resolve();
      }
      return websocketProvider.connect();
    },
    detach: () => {
      detached = true;
      unregisterFromBroker?.();
      unregisterFromBroker = null;
      provider.detach();
    },
    destroy: () => {
      detached = true;
      clearConnectionTimeout();
      releaseBroker();
      provider.awareness?.off("change", onAwarenessChange);
      provider.destroy();
      if (ownsWebsocketProvider) {
        websocketProvider.destroy();
      }
    },
  };
}
